import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axiosConfig from '../config/axiosConfig';

const OnboardingContext = createContext(null);

/**
 * Returns the onboarding status for the logged-in company.
 * Safe to call outside the provider — returns sensible defaults.
 *
 * Shape of `onboardingStatus`:
 *   has_active_plan       : boolean
 *   plan_name             : string | null
 *   waba_setup_complete   : boolean
 *   waba_status           : 'active' | 'pending' | 'inactive' | null
 *   email_enabled         : boolean
 *   whatsapp_enabled      : boolean
 *   modules: {
 *     email, whatsapp, campaigns, templates, inbox:
 *       { accessible: boolean, reason: string }
 *   }
 */
export const useOnboarding = () => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    // Outside provider — grant full access (e.g., admin or unauthenticated pages)
    return {
      onboardingStatus: null,
      loadingOnboarding: false,
      refreshOnboarding: () => Promise.resolve(),
    };
  }
  return ctx;
};

export const OnboardingProvider = ({ children }) => {
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loadingOnboarding, setLoadingOnboarding] = useState(true);

  const fetchOnboardingStatus = useCallback(async () => {
    try {
      setLoadingOnboarding(true);
      const res = await axiosConfig.get('/company/onboarding-status');
      // Support both entity and data wrappers
      const data = res.data?.entity ?? res.data?.data ?? null;
      setOnboardingStatus(data);
    } catch (err) {
      // Non-fatal — fail open so we don't block the dashboard
      console.error('Error fetching onboarding status:', err);
      setOnboardingStatus(null);
    } finally {
      setLoadingOnboarding(false);
    }
  }, []);

  useEffect(() => {
    fetchOnboardingStatus();
  }, [fetchOnboardingStatus]);

  return (
    <OnboardingContext.Provider
      value={{
        onboardingStatus,
        loadingOnboarding,
        refreshOnboarding: fetchOnboardingStatus,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
